import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, Star } from "lucide-react";
import { CONTACT } from "@/lib/data";
import { scrollToId } from "@/components/Logo";

const MAIN_IMG =
  "https://images.unsplash.com/photo-1665960213508-48f07086d49c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const SUB_IMG =
  "https://images.unsplash.com/photo-1708606811579-23b18fc48007?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.3 + i * 0.12 },
  }),
};

const Line = ({ children, i }) => (
  <span className="reveal-mask">
    <motion.span className="block" variants={lineReveal} custom={i}>
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const mainY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const subY = useTransform(scrollYProgress, [0, 1], ["0%", "-22%"]);

  return (
    <section
      id="home"
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden grain bg-[var(--bg)]"
      data-testid="hero-section"
    >
      {/* oversized watermark */}
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.04 }}
        transition={{ delay: 0.6, duration: 1.2 }}
        className="pointer-events-none select-none absolute -top-4 left-1/2 -translate-x-1/2 font-serif italic text-[22vw] leading-none text-[var(--ink)] whitespace-nowrap"
      >
        Temple Bells
      </motion.span>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 min-h-[100svh] grid lg:grid-cols-2 gap-10 lg:gap-16 items-center pt-28 pb-16">
        {/* Left — copy */}
        <div className="order-2 lg:order-1">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.8 }}
            className="flex items-center gap-3 mb-6"
          >
            <span className="h-px w-10 bg-[var(--gold)]" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-[var(--ink-2)]">
              {CONTACT.locationLine}
            </span>
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="show"
            className="font-serif text-[var(--ink)] text-5xl sm:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight"
            data-testid="hero-heading"
          >
            <Line i={0}>Sacred</Line>
            <Line i={1}>
              ceremonies, <span className="italic text-[var(--gold)]">artfully</span>
            </Line>
            <Line i={2}>curated.</Line>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9 }}
            className="mt-7 max-w-md text-base sm:text-lg text-[var(--ink-2)] font-light leading-relaxed"
          >
            A full-service wedding atelier crafting unforgettable celebrations — planning, design,
            catering and cinematic film, woven into one seamless story.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToId("contact")}
              className="group inline-flex items-center gap-3 rounded-full bg-[var(--ink)] text-[var(--bg)] px-8 py-4 text-sm tracking-wide hover:bg-[var(--gold)] transition-colors duration-400"
              data-testid="hero-plan-wedding-button"
            >
              Plan Your Wedding
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--gold)] group-hover:bg-[var(--bg)] transition-colors" />
            </button>
            <button
              onClick={() => scrollToId("gallery")}
              className="link-underline text-sm tracking-wide text-[var(--ink)]"
              data-testid="hero-gallery-button"
            >
              View Our Work
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.35, duration: 0.9 }}
            className="mt-10 flex items-center gap-4"
          >
            <div className="flex text-[var(--gold)]">
              {[0, 1, 2, 3, 4].map((s) => (
                <Star key={s} size={15} fill="currentColor" strokeWidth={0} />
              ))}
            </div>
            <span className="text-sm text-[var(--ink-2)]">
              <span className="font-serif text-[var(--ink)] text-base">250+</span> celebrations ·{" "}
              <span className="font-serif text-[var(--ink)] text-base">12</span> years of craft
            </span>
          </motion.div>
        </div>

        {/* Right — image composition */}
        <div className="order-1 lg:order-2 relative h-[46svh] sm:h-[60svh] lg:h-[78svh]">
          <motion.div
            style={{ y: mainY }}
            initial={{ opacity: 0, scale: 1.06, clipPath: "inset(12% 0% 0% 0%)" }}
            animate={{ opacity: 1, scale: 1, clipPath: "inset(0% 0% 0% 0%)" }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.25 }}
            className="absolute top-0 right-0 w-[82%] h-[86%] overflow-hidden rounded-[3px] shadow-[0_40px_90px_-50px_rgba(35,31,32,0.6)]"
          >
            <img src={MAIN_IMG} alt="Wedding couple" className="w-full h-full object-cover" />
          </motion.div>

          <motion.div
            style={{ y: subY }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.7 }}
            className="absolute bottom-0 left-0 w-[46%] h-[42%] overflow-hidden rounded-[3px] border-4 border-[var(--bg)] shadow-[0_30px_60px_-40px_rgba(35,31,32,0.6)]"
          >
            <img src={SUB_IMG} alt="Wedding mandap decor" className="w-full h-full object-cover" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 1 }}
            className="absolute top-[8%] left-[2%] bg-[var(--bg)] border border-[var(--border-gold)] rounded-full px-5 py-3 shadow-[0_20px_50px_-30px_rgba(35,31,32,0.5)]"
          >
            <div className="font-accent text-[10px] tracking-[0.25em] text-[var(--ink-3)]">EST.</div>
            <div className="font-serif text-xl text-[var(--gold)] leading-none">Bengaluru</div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[var(--ink-2)]"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
}
